import { Heart, Home, Plus, BookHeart } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { BrandLogo } from '@/components/BrandLogo';
import type { SavedItem } from '@/pages/SavedMessages';
import { detectCategory, CATEGORY_ICONS } from '@/pages/SavedMessages';

function timeAgoShort(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

interface Props {
  totalSaved: number;
  lastSavedDate: string | null;
  selectedFilter: string | null;
  onFilterChange: (filter: string | null) => void;
  items: SavedItem[];
}

export function SavedDesktopSidebar({ totalSaved, lastSavedDate, selectedFilter, onFilterChange, items }: Props) {
  const location = useLocation();

  const categoryCounts = items.reduce<Record<string, number>>((acc, item) => {
    const cat = detectCategory(item);
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  const navItems = [
    { label: 'Home', icon: Home, to: '/dashboard' },
    { label: 'Create', icon: Plus, to: '/create' },
    { label: 'My Life Vault', icon: BookHeart, to: '/saved', active: true },
  ];

  return (
    <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-[280px] bg-[hsl(220,25%,6%)]/90 backdrop-blur-xl border-r border-white/5 z-40">
      {/* Logo */}
      <div className="p-5 pb-3">
        <Link to="/dashboard">
          <BrandLogo size="sm" />
        </Link>
      </div>

      {/* Nav */}
      <nav className="px-3 space-y-0.5 mb-6">
        {navItems.map(item => (
          <Link key={item.to} to={item.to}>
            <div className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
              item.active
                ? "bg-white/10 text-white"
                : "text-white/40 hover:text-white/70 hover:bg-white/5"
            )}>
              <item.icon className="h-4 w-4" />
              {item.label}
            </div>
          </Link>
        ))}
      </nav>

      {/* Stats */}
      <div className="px-5 py-4 mx-3 rounded-2xl bg-white/5 border border-white/5">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="h-4 w-4 text-rose-400 fill-current" />
          <span className="text-white/80 text-sm font-semibold">{totalSaved} Moments Saved</span>
        </div>
        {lastSavedDate && (
          <p className="text-[11px] text-white/65">
            Last saved: {timeAgoShort(lastSavedDate)}
          </p>
        )}
      </div>

      {/* Category filters */}
      {Object.keys(categoryCounts).length > 0 && (
        <div className="px-5 mt-5">
          <p className="text-[11px] text-white/55 uppercase tracking-wider mb-2 font-medium">Categories</p>
          <div className="space-y-1">
            <button
              onClick={() => onFilterChange(null)}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all",
                !selectedFilter ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
              )}
            >
              <span>All</span>
              <span className="text-[11px] text-white/60">{totalSaved}</span>
            </button>
            {Object.entries(categoryCounts).map(([cat, count]) => (
              <button
                key={cat}
                onClick={() => onFilterChange(selectedFilter === cat ? null : cat)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all",
                  selectedFilter === cat ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
                )}
              >
                <span>{CATEGORY_ICONS[cat]} {cat.charAt(0).toUpperCase() + cat.slice(1)}</span>
                <span className="text-[11px] text-white/60">{count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom quote */}
      <div className="mt-auto p-5">
        <p className="text-[11px] text-white/15 italic leading-relaxed">
          "The moments we save define who we are."
        </p>
      </div>
    </aside>
  );
}
